import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Tag } from '../../models/interfaces/tag.interface';
import { TagPayload } from '../../types/payloads/tag.payload';
import { tagsMock } from '../../mocks/interfaces/tag.mock';
import { coursesMock } from '../../mocks/interfaces/course.mock';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { createCrudUrl, CrudRequestCondition, CrudRequestParams } from '../pagination/models/interfaces/crud';
import { Api } from '../api/api';
import { isFakeBackendEnabled } from '../../utils/fake-backend.util';

export const TAG_NAME_MAX_LENGTH = 50;

export const COURSE_TAGS_MAX = 30;

/**
 * Normaliza o nome da tag da mesma forma que a API Learning (trim + minúsculas).
 */
export function normalizeTagName(name: string): string {
  return name.trim().toLowerCase();
}

@Injectable({
  providedIn: 'root',
})
export class TagService {

  private readonly api = inject(Api);

  private readonly baseRoute = '/tags';

  public async getManyMocked(params?: CrudRequestParams<Tag>): Promise<PaginationDto<Tag>> {
    const page = Math.max(1, params?.page ?? 1);
    const limit = Math.max(1, params?.limit ?? 25);
    const search = this.getSearchValue(params?.search);

    const filtered = tagsMock.filter((tag) => tag.isActive && (!search || tag.name.includes(search)));

    const sortRule = params?.sort?.[0];
    const sorted = [...filtered].sort((a, b) => {
      if (!sortRule?.field) {
        return 0;
      }

      const direction = sortRule.order === 'ASC' ? 1 : -1;
      const field = sortRule.field as keyof Tag;
      const left = a[field] instanceof Date ? (a[field] as Date).toISOString() : String(a[field] ?? '');
      const right = b[field] instanceof Date ? (b[field] as Date).toISOString() : String(b[field] ?? '');
      return left.localeCompare(right) * direction;
    });

    const total = sorted.length;
    const pageCount = Math.max(1, Math.ceil(total / limit));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * limit;
    const data = sorted.slice(start, start + limit);

    return {
      data,
      page: safePage,
      pageCount,
      count: data.length,
      total,
    };
  }

  public async findByNameMocked(name: string): Promise<Tag | null> {
    const normalized = normalizeTagName(name);
    return tagsMock.find((tag) => tag.name === normalized) ?? null;
  }

  public async createMocked(payload: TagPayload): Promise<Tag> {
    const name = this.assertValidName(payload.name);
    this.assertNameAvailableMocked(name);

    const now = new Date();
    const created: Tag = {
      id: `tag-${crypto.randomUUID()}`,
      tenantId: 'tenant-uuid',
      isActive: true,
      name,
      createdAt: now,
      updatedAt: now,
    };

    tagsMock.unshift(created);

    return created;
  }

  public async updateMocked(tagId: string, payload: TagPayload): Promise<Tag> {
    const targetIndex = tagsMock.findIndex((tag) => tag.id === tagId);

    if (targetIndex < 0) {
      throw new HttpErrorResponse({ status: 404, error: { message: 'Tag não encontrada para edição.' } });
    }

    const current = tagsMock[targetIndex];
    const name = this.assertValidName(payload.name);

    if (name !== current.name) {
      this.assertNameAvailableMocked(name);
    }

    const updated: Tag = {
      ...current,
      name,
      updatedAt: new Date(),
    };

    tagsMock[targetIndex] = updated;

    return updated;
  }

  public async deleteMocked(tagId: string): Promise<void> {
    const target = tagsMock.find((tag) => tag.id === tagId);

    if (!target) {
      throw new HttpErrorResponse({ status: 404, error: { message: 'Tag não encontrada.' } });
    }

    const isInUse = coursesMock.some((course) => course.tags?.some((tag) => tag.id === tagId));

    if (isInUse) {
      throw new HttpErrorResponse({
        status: 409,
        error: { message: 'A tag não pode ser removida pois está associada a cursos.' },
      });
    }

    target.isActive = false;
    target.updatedAt = new Date();
  }

  // ─── Public API (fake backend ou integração real) ─────────────────────────

  public async getMany(params?: CrudRequestParams<Tag>): Promise<PaginationDto<Tag>> {
    if (isFakeBackendEnabled()) {
      return this.getManyMocked(params);
    }

    const url = createCrudUrl<Tag>(this.baseRoute, params);
    const response = await this.api.getAsync<PaginationDto<Tag>>(url);

    if (!response) {
      return {
        data: [],
        page: 0,
        pageCount: 0,
        count: 0,
        total: 0,
      };
    }

    return response;
  }

  /**
   * Lista as tags ativas do tenant, opcionalmente filtradas pelo nome.
   */
  public async searchActive(term = '', limit = 100): Promise<Tag[]> {
    const response = await this.getMany({
      page: 1,
      limit,
      search: this.buildActiveSearch(term),
      sort: [{ field: 'name', order: 'ASC' }],
    });

    return response.data;
  }

  /**
   * Busca uma tag (ativa ou desativada) pelo nome exato, já normalizado.
   */
  public async findByName(name: string): Promise<Tag | null> {
    if (isFakeBackendEnabled()) {
      return this.findByNameMocked(name);
    }

    const response = await this.getMany({
      page: 1,
      limit: 1,
      search: { name: { $eq: normalizeTagName(name) } },
    });

    return response.data[0] ?? null;
  }

  public async create(payload: TagPayload): Promise<Tag> {
    if (isFakeBackendEnabled()) {
      return this.createMocked(payload);
    }

    const response = await this.api.postAsync<Tag>(this.baseRoute, { name: payload.name.trim() });

    if (!response) {
      throw new Error('Falha ao criar a tag.');
    }

    return response;
  }

  public async update(tagId: string, payload: TagPayload): Promise<Tag> {
    if (isFakeBackendEnabled()) {
      return this.updateMocked(tagId, payload);
    }

    const response = await this.api.putAsync<Tag>(`${this.baseRoute}/${tagId}`, { name: payload.name.trim() });

    if (!response) {
      throw new Error('Falha ao atualizar a tag.');
    }

    return response;
  }

  public async delete(tagId: string): Promise<void> {
    if (isFakeBackendEnabled()) {
      return this.deleteMocked(tagId);
    }

    await this.api.deleteAsync(`${this.baseRoute}/${tagId}`);
  }

  private buildActiveSearch(term: string): CrudRequestCondition<Tag> {
    const searchValue = normalizeTagName(term);

    if (!searchValue) {
      return { isActive: { $eq: true } };
    }

    return {
      isActive: { $eq: true },
      name: { $contL: searchValue },
    };
  }

  private assertValidName(name: string): string {
    const normalized = normalizeTagName(name);

    if (!normalized) {
      throw new HttpErrorResponse({ status: 400, error: { message: 'O nome da tag não pode ser vazio.' } });
    }

    return normalized;
  }

  private assertNameAvailableMocked(name: string): void {
    const existing = tagsMock.find((tag) => tag.name === name);

    if (!existing) {
      return;
    }

    const message = existing.isActive
      ? `Já existe uma tag com o nome "${name}".`
      : `A tag "${name}" está desativada. Para reutilizá-la, ative-a novamente.`;

    throw new HttpErrorResponse({ status: 409, error: { message } });
  }

  private getSearchValue(search?: CrudRequestParams<Tag>['search']): string {
    if (!search || typeof search !== 'object') {
      return '';
    }

    const field = (search as Record<string, unknown>)['name'];

    if (!field || typeof field !== 'object') {
      return '';
    }

    const operators = field as Record<string, unknown>;
    const value = operators['$contL'] ?? operators['$cont'] ?? operators['$eq'];

    return typeof value === 'string' ? normalizeTagName(value) : '';
  }
}
